import React, {Fragment, useContext, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import glamorous from 'glamorous';
import {ASH, CHARCOAL} from '../../../../shared/style/colors';
import ToolCardLoader from './loader';
import {formatCount} from '../../../../shared/utils/format';
import {BASE_TEXT, WEIGHT} from '../../../../shared/style/typography';
import FollowServiceButton from '../../../../shared/library/buttons/follow/follow-service-button';
import UseThisButton from '../../../../shared/library/buttons/use-this';
import ContactVendorCard from '../../../../shared/library/cards/contact-vendor';
import ToolStatsCard from '../../../../shared/library/cards/tool-stats';
import CloseIcon from '../../../../shared/library/icons/close.svg';
import {PHONE_LANDSCAPE} from '../../../../shared/style/breakpoints';
import {VENDOR_CTA} from '../../../../shared/library/advert/themes';
import Hint, {ACTIVATE_MODE_HOVER} from '../../../../shared/library/popovers/hint';
import {BOTTOM} from '../../../../shared/constants/placements';
import {MobileContext} from '../../../../shared/enhancers/mobile-enhancer';
import {PrivateModeContext} from '../../../../shared/enhancers/private-mode-enchancer';
import {planPermissionCheck} from '../../../../shared/utils/plan-permission-check';
import {PLANS} from '../../../private-payment/constants';
import {ApolloContext} from '../../../../shared/enhancers/graphql-enhancer';
import {trackEvent} from '../../../../data/shared/mutations';
import {VENDOR_AD_IMPRESSION} from '../../../../shared/constants/analytics';

const Container = glamorous.div(
  {
    background: 'white',
    borderRadius: 2,
    width: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    height: '100%'
  },
  ({minimal}) => ({
    justifyContent: minimal ? 'flex-start' : 'flex-end',
    paddingTop: minimal ? 0 : 27,
    paddingBottom: minimal ? 0 : 30,
    border: minimal ? 0 : `1px solid ${ASH}`,
    boxShadow: minimal ? 0 : `0 1px 0 0 ${ASH}`,
    '>button': {
      top: minimal ? 16.5 : 10,
      right: minimal ? 14 : 10,
      [PHONE_LANDSCAPE]: {
        right: 0
      }
    }
  })
);

const MinimalContainer = glamorous.div({
  borderRadius: 2,
  width: '100%',
  display: 'flex',
  position: 'relative',
  border: `1px solid ${ASH}`,
  padding: 6,
  boxSizing: 'border-box',
  paddingRight: 36,
  [PHONE_LANDSCAPE]: {
    border: 0,
    ' > div': {
      display: 'none'
    }
  }
});

const MinimalMeta = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  ...BASE_TEXT,
  color: CHARCOAL,
  fontWeight: WEIGHT.BOLD,
  fontSize: 16,
  lineHeight: 1.38,
  textDecoration: 'none',
  '>span:nth-child(2)': {
    fontSize: 13,
    color: '#ababab',
    fontWeight: WEIGHT.NORMAL
  },
  [PHONE_LANDSCAPE]: {
    '>span:nth-child(1)': {
      fontSize: 14
    },
    '>span:nth-child(2)': {
      fontSize: 13
    }
  }
});

const Logo = glamorous.a(
  {
    ...BASE_TEXT,
    color: CHARCOAL,
    fontWeight: WEIGHT.BOLD,
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    textDecoration: 'none'
  },
  ({minimal}) => ({
    marginBottom: minimal ? 0 : 24,
    flexGrow: minimal ? 0 : 1,
    order: minimal ? 0 : 1,
    flexDirection: minimal ? 'row' : 'column'
  })
);

const LogoImage = glamorous.div(({src, minimal}) => ({
  border: 'solid 1px #e5e5e5',
  borderRadius: 4,
  width: minimal ? 38 : 84,
  height: minimal ? 38 : 84,
  marginRight: minimal ? 11 : 0,
  marginBottom: minimal ? 0 : 5,
  backgroundImage: `url(${src})`,
  backgroundRepeat: 'none',
  backgroundSize: 'contain'
}));

const Actions = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  order: 2
});

const ActionRow = glamorous.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-around',
  flexWrap: 'wrap',
  marginTop: 15,
  marginBottom: 32,
  ' > button:first-of-type': {
    marginRight: 15
  }
});

const HorizontalRule = glamorous.hr({
  border: 0,
  borderBottom: `1px solid ${ASH}`,
  height: 1,
  width: '90%',
  marginTop: -10,
  marginBottom: 17,
  order: 3
});

const RemoveButton = glamorous.button({
  padding: 0,
  outline: 'none',
  border: 0,
  width: 20,
  height: 20,
  borderRadius: '50%',
  background: '#dbdbdb',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '>svg': {
    width: 8,
    height: 8
  },
  position: 'absolute',
  cursor: 'pointer'
});

const ContactWrapper = glamorous.div({
  marginLeft: 'auto'
});
const renderLoader = minimal => <ToolCardLoader animate={true} minimal={minimal} />;

const renderEmpty = (onChange, minimal, focus, otherToolSlugs) => (
  <ToolCardLoader
    animate={false}
    onChange={onChange}
    minimal={minimal}
    focus={focus}
    otherToolSlugs={otherToolSlugs}
  />
);

const renderTool = (
  tool,
  stackupPath,
  minimal,
  vendorInStackup,
  tooltip = false,
  contactEnabledTools = [],
  userIsAdmin,
  triggerEvent = false
) => {
  const [animateFollowStat, setAnimateFollowStat] = useState(false);
  const privateMode = useContext(PrivateModeContext);
  const isMobile = useContext(MobileContext);
  const client = useContext(ApolloContext);
  const planPermission = planPermissionCheck(privateMode, PLANS.FREE);
  const contactEnabled =
    tool.contactEnabled && contactEnabledTools && contactEnabledTools.some(t => t.id === tool.id);
  const sponsorId = tool && tool.contactButtonSponsorId;

  const onToggle = () => {
    if (!tool.followingTool) {
      setAnimateFollowStat(true);
    } else {
      setAnimateFollowStat(false);
    }
  };

  useEffect(() => {
    if (sponsorId && triggerEvent && stackupPath) {
      client.mutate({
        mutation: trackEvent,
        variables: {
          eventSubtype: VENDOR_AD_IMPRESSION,
          adId: sponsorId
        }
      });
    }
  }, [stackupPath]);

  const vendorAnalyticsData = {
    path: stackupPath,
    contentGroupPage: 'Stackups',
    sponsor: {
      name: tool.name,
      serviceId: tool.legacyThirdPartyId,
      featured: null,
      text: null,
      url: null
    },
    theme: VENDOR_CTA,
    layer: tool.layer && tool.layer.name,
    category: tool.categeory && tool.category.name,
    primaryFunction: tool.function && tool.function.name
  };
  const {contactButtonText, contactFlow} = tool;

  const image = isMobile && tool.thumbRetinaUrl ? tool.thumbRetinaUrl : tool.imageUrl;
  if (minimal) {
    return (
      <MinimalContainer>
        <Logo minimal={true} href={tool.canonicalUrl}>
          <LogoImage alt={tool.name} src={image} minimal={true} />
          <MinimalMeta>
            <span>{tool.name}</span>
            <span>{formatCount(tool.stacks)} Stacks</span>
          </MinimalMeta>
        </Logo>
        <ContactWrapper>
          {vendorInStackup && tooltip && (
            <Hint
              activateMode={ACTIVATE_MODE_HOVER}
              placement={BOTTOM}
              anchor={
                (!privateMode || userIsAdmin || planPermission) && (
                  <ContactVendorCard
                    toolId={tool.id}
                    contactEnabled={contactEnabled}
                    contactButtonText={contactButtonText}
                    contactFlow={contactFlow}
                    minimal={true}
                    vendorName={tool.name}
                    analyticsData={vendorAnalyticsData}
                    sponsorId={sponsorId}
                  />
                )
              }
              hint={contactButtonText}
            />
          )}
        </ContactWrapper>
        {vendorInStackup && !tooltip && planPermission && (
          <ContactVendorCard
            toolId={tool.id}
            contactEnabled={contactEnabled}
            contactButtonText={contactButtonText}
            contactFlow={contactFlow}
            minimal={true}
            vendorName={tool.name}
            analyticsData={vendorAnalyticsData}
            sponsorId={sponsorId}
          />
        )}
      </MinimalContainer>
    );
  }
  return (
    <Fragment>
      <Logo href={tool.canonicalUrl}>
        <LogoImage alt={tool.name} src={image} />
        <div>{tool.name}</div>
      </Logo>
      <Actions>
        {vendorInStackup && (!privateMode || userIsAdmin || planPermission) && (
          <ContactVendorCard
            toolId={tool.id}
            contactEnabled={contactEnabled}
            contactButtonText={contactButtonText}
            contactFlow={contactFlow}
            withPlaceholder={true}
            vendorName={tool.name}
            analyticsData={vendorAnalyticsData}
            sponsorId={sponsorId}
          />
        )}
        <ActionRow>
          <FollowServiceButton
            serviceId={tool.id}
            following={tool.followingTool}
            onToggle={onToggle}
          />
          <UseThisButton serviceId={tool.id} customStyle={{zIndex: 1}} />
        </ActionRow>
      </Actions>
      <HorizontalRule />
      <ToolStatsCard
        stacks={tool.stacks}
        votes={tool.votes}
        followers={tool.followersCount}
        animateFollowStat={animateFollowStat}
      />
    </Fragment>
  );
};

const ToolCard = ({
  tool,
  onChange = null,
  onRemove = null,
  minimal = false,
  focus,
  otherToolSlugs,
  contactEnabledTools,
  stackupPath,
  vendorInStackup = false,
  tooltip,
  userIsAdmin,
  triggerEvent
}) => {
  let content;
  if (tool) {
    content = renderTool(
      tool,
      stackupPath,
      minimal,
      vendorInStackup,
      tooltip,
      contactEnabledTools,
      userIsAdmin,
      triggerEvent
    );
  } else {
    content = onChange
      ? renderEmpty(onChange, minimal, focus, otherToolSlugs)
      : renderLoader(minimal);
  }
  return (
    <Container minimal={minimal}>
      {content}
      {tool && onRemove && (
        <RemoveButton data-testid="removeTool" onClick={onRemove}>
          <CloseIcon />
        </RemoveButton>
      )}
    </Container>
  );
};

ToolCard.propTypes = {
  tool: PropTypes.object,
  onChange: PropTypes.func,
  minimal: PropTypes.bool,
  focus: PropTypes.bool,
  onRemove: PropTypes.func,
  otherToolSlugs: PropTypes.array,
  stackupPath: PropTypes.string,
  vendorInStackup: PropTypes.bool,
  tooltip: PropTypes.bool,
  contactEnabledTools: PropTypes.array,
  userIsAdmin: PropTypes.bool,
  triggerEvent: PropTypes.bool
};

export default ToolCard;
